import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfig } from '@env/app-config';
import { SUPPRESS_HTTP_ERRORS } from '@shared/auth/interceptors/error.interceptor';
import {
  AcceptedView, AuthView, CustomerPreferences, CustomerView, GuestContactRequest, LoginRequest,
  PersonLookup, RecoveryCodesResponse, RegisterBusinessRequest, RegisterPersonalRequest, RucLookup, TrustedDevice,
  TwoFactorReauth, TwoFactorSetup, TwoFactorStatus, UpdateFiscalAddressRequest, UpdateProfileRequest
} from '../domain/customer.model';
import { ctxTienda } from '../utils/storefront-context.util';

/**
 * Endpoints de `/api/prosell/storefront/customer-auth/**`. Todas las llamadas llevan la marca de tienda
 * (el interceptor añade `X-Customer-Surface` y credenciales) y suprimen el toast global: cada pantalla pinta
 * su error por campo con `parseAuthError`.
 */
@Injectable({ providedIn: 'root' })
export class CustomerAuthApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${AppConfig.apiUrl}/api/prosell/storefront/customer-auth`;

  private ctx() {
    return ctxTienda().set(SUPPRESS_HTTP_ERRORS, true);
  }

  registerPersonal(body: RegisterPersonalRequest): Observable<AcceptedView> {
    return this.http.post<AcceptedView>(`${this.base}/register/personal`, body, { context: this.ctx() });
  }

  registerBusiness(body: RegisterBusinessRequest): Observable<AcceptedView> {
    return this.http.post<AcceptedView>(`${this.base}/register/business`, body, { context: this.ctx() });
  }

  login(body: LoginRequest): Observable<AuthView> {
    return this.http.post<AuthView>(`${this.base}/login`, body, { context: this.ctx() });
  }

  verifyTwoFactor(body: { challengeToken: string; code: string; method: 'TOTP' | 'EMAIL' | 'RECOVERY'; rememberDevice?: boolean }): Observable<AuthView> {
    return this.http.post<AuthView>(`${this.base}/2fa/verify`, body, { context: this.ctx() });
  }

  sendTwoFactorEmail(challengeToken: string): Observable<void> {
    return this.http.post<void>(`${this.base}/2fa/send-email`, { challengeToken }, { context: this.ctx() });
  }

  guest(): Observable<AuthView> {
    return this.http.post<AuthView>(`${this.base}/guest`, {}, { context: this.ctx() });
  }

  updateGuestContact(body: GuestContactRequest): Observable<CustomerView> {
    return this.http.put<CustomerView>(`${this.base}/guest/contact`, body, { context: this.ctx() });
  }

  refresh(): Observable<AuthView> {
    return this.http.post<AuthView>(`${this.base}/refresh`, {}, { context: this.ctx() });
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.base}/logout`, {}, { context: this.ctx() });
  }

  /** Ayuda de autocompletado: puede fallar con 404/429/503 y entonces se completa a mano. */
  lookupRuc(ruc: string): Observable<RucLookup> {
    return this.http.get<RucLookup>(`${AppConfig.apiUrl}/api/prosell/storefront/consulta-ruc/${encodeURIComponent(ruc)}`, { context: this.ctx() });
  }

  lookupDni(dni: string): Observable<PersonLookup> {
    return this.http.get<PersonLookup>(`${AppConfig.apiUrl}/api/prosell/storefront/consulta-dni/${encodeURIComponent(dni)}`, { context: this.ctx() });
  }

  verifyEmail(token: string): Observable<void> {
    return this.http.post<void>(`${this.base}/verify-email`, { token }, { context: this.ctx() });
  }

  resendVerification(): Observable<void> {
    return this.http.post<void>(`${this.base}/resend-verification`, {}, { context: this.ctx() });
  }

  forgotPassword(email: string): Observable<AcceptedView> {
    return this.http.post<AcceptedView>(`${this.base}/forgot-password`, { email }, { context: this.ctx() });
  }

  resetPassword(token: string, password: string): Observable<void> {
    return this.http.post<void>(`${this.base}/reset-password`, { token, password }, { context: this.ctx() });
  }

  updateProfile(body: UpdateProfileRequest): Observable<CustomerView> {
    return this.http.patch<CustomerView>(`${this.base}/me`, body, { context: this.ctx() });
  }

  updateFiscalAddress(body: UpdateFiscalAddressRequest): Observable<CustomerView> {
    return this.http.patch<CustomerView>(`${this.base}/me/fiscal-address`, body, { context: this.ctx() });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.base}/me/password`, { currentPassword, newPassword }, { context: this.ctx() });
  }

  /** Con el 2FA ya activo, empezar el reemplazo de la app exige contraseña y un código actual. */
  setupTwoFactor(reauth?: TwoFactorReauth): Observable<TwoFactorSetup> {
    return this.http.post<TwoFactorSetup>(`${this.base}/2fa/setup`, reauth ?? {}, { context: this.ctx() });
  }

  /** Devuelve los códigos de recuperación (una sola vez). */
  confirmTwoFactor(code: string): Observable<RecoveryCodesResponse> {
    return this.http.post<RecoveryCodesResponse>(`${this.base}/2fa/confirm`, { code }, { context: this.ctx() });
  }

  disableTwoFactor(reauth: TwoFactorReauth): Observable<void> {
    return this.http.post<void>(`${this.base}/2fa/disable`, reauth, { context: this.ctx() });
  }

  regenerateRecoveryCodes(reauth: TwoFactorReauth): Observable<RecoveryCodesResponse> {
    return this.http.post<RecoveryCodesResponse>(`${this.base}/2fa/recovery-codes`, reauth, { context: this.ctx() });
  }

  setEmailFallback(enabled: boolean, password: string): Observable<TwoFactorStatus> {
    return this.http.patch<TwoFactorStatus>(`${this.base}/2fa/email-fallback`, { enabled, password }, { context: this.ctx() });
  }

  twoFactorStatus(): Observable<TwoFactorStatus> {
    return this.http.get<TwoFactorStatus>(`${this.base}/2fa/status`, { context: this.ctx() });
  }

  trustedDevices(): Observable<TrustedDevice[]> {
    return this.http.get<TrustedDevice[]>(`${this.base}/2fa/devices`, { context: this.ctx() });
  }

  revokeTrustedDevice(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/2fa/devices/${encodeURIComponent(id)}`, { context: this.ctx() });
  }

  revokeAllTrustedDevices(): Observable<void> {
    return this.http.delete<void>(`${this.base}/2fa/devices`, { context: this.ctx() });
  }

  getPreferences(): Observable<CustomerPreferences> {
    return this.http.get<CustomerPreferences>(`${AppConfig.apiUrl}/api/prosell/customer-preferences`, { context: this.ctx() });
  }

  updatePreferences(body: CustomerPreferences): Observable<CustomerPreferences> {
    return this.http.put<CustomerPreferences>(`${AppConfig.apiUrl}/api/prosell/customer-preferences`, body, { context: this.ctx() });
  }

  me(): Observable<CustomerView> {
    return this.http.get<CustomerView>(`${this.base}/me`, { context: this.ctx() });
  }
}
